import { useState, useCallback } from 'react';
import html2canvas from 'html2canvas';

export const useImageManipulation = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageProperties, setImageProperties] = useState({});

  const selectImage = useCallback((imageElement, dimensions = null) => {
    if (!imageElement) {
      setSelectedImage(null);
      return;
    }

    setSelectedImage(imageElement);

    // Use provided dimensions or get from element
    const width = dimensions?.width || imageElement.offsetWidth;
    const height = dimensions?.height || imageElement.offsetHeight;

    setImageProperties({
      width,
      height,
      rotation: 0,
      flipHorizontal: false,
      flipVertical: false,
    });
  }, []);

  const rotateImage = useCallback((angle) => {
    if (!selectedImage) return;

    const currentRotation = imageProperties.rotation || 0;
    const newRotation = (currentRotation + angle) % 360;

    setImageProperties((prev) => ({
      ...prev,
      rotation: newRotation,
    }));

    selectedImage.style.transform = `rotate(${newRotation}deg)${
      imageProperties.flipHorizontal ? ' scaleX(-1)' : ''
    }${imageProperties.flipVertical ? ' scaleY(-1)' : ''}`;
  }, [selectedImage, imageProperties]);

  const flipImage = useCallback((direction) => {
    if (!selectedImage) return;

    const isHorizontal = direction === 'horizontal';
    const flipKey = isHorizontal ? 'flipHorizontal' : 'flipVertical';
    const newFlipValue = !imageProperties[flipKey];

    setImageProperties((prev) => ({
      ...prev,
      [flipKey]: newFlipValue,
    }));

    const scaleX = isHorizontal && newFlipValue ? -1 :
                   isHorizontal && !newFlipValue ? 1 :
                   imageProperties.flipHorizontal ? -1 : 1;

    const scaleY = !isHorizontal && newFlipValue ? -1 :
                   !isHorizontal && !newFlipValue ? 1 :
                   imageProperties.flipVertical ? -1 : 1;

    selectedImage.style.transform = `rotate(${imageProperties.rotation || 0}deg) scaleX(${scaleX}) scaleY(${scaleY})`;
  }, [selectedImage, imageProperties]);

  const resizeImage = useCallback((width, height) => {
    if (!selectedImage) return;

    setImageProperties((prev) => ({
      ...prev,
      width,
      height,
    }));

    selectedImage.style.width = `${width}px`;
    selectedImage.style.height = `${height}px`;
  }, [selectedImage]);

  const removeBackground = useCallback(async (imageElement) => {
    // This is a placeholder for background removal
    // In production, you would integrate with an API like remove.bg
    try {
      const canvas = await html2canvas(imageElement);
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Simple white background removal (placeholder)
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        // If pixel is close to white, make it transparent
        if (r > 240 && g > 240 && b > 240) {
          data[i + 3] = 0;
        }
      }

      ctx.putImageData(imageData, 0, 0);
      return canvas.toDataURL();
    } catch (error) {
      console.error('Error removing background:', error);
      return null;
    }
  }, []);

  const changeBackground = useCallback((imageElement, backgroundColor) => {
    if (!imageElement) return;

    imageElement.style.backgroundColor = backgroundColor;
  }, []);

  return {
    selectedImage,
    selectImage,
    imageProperties,
    rotateImage,
    flipImage,
    resizeImage,
    removeBackground,
    changeBackground,
  };
};
